import classnames from 'classnames';
import dynamic from 'next/dynamic';
import { FC, useState } from 'react';

import { isB2B, isB2C } from '@/core/settings/utils';
import { Text } from '@/core/tamagoshi/Text/Text';
import { Image } from '@/domains/core/Image/Image';
import { Link as TypographyLink } from '@/domains/core/page/components/Typography/Link';
import { Link } from '@/domains/core/routing/components/Link';
import { Gtm } from '@/domains/core/tracking/utils/Gtm';
import { useDetail } from '@/domains/productDiscovery/ProductPage/hooks/useDetail';
import { useBrand } from '@/domains/productDiscovery/ProductPage/modules/Brand/useBrand';
import type { OtherSellersWrapperProps } from '@/productDiscovery/ProductPage/modules/MainDetails/components/Seller/OtherSellersWrapper';

import type { OtherSellersProps } from './OtherSellers';
import { discoverTheStore, otherVendors, soldBy } from './translations';

import styles from './seller.module.scss';

const SellersModalB2C = dynamic<OtherSellersProps>(
  () => import('./OtherSellers').then((mod) => mod.OtherSellersModal),
  { ssr: false },
);

const SellersModalB2B = dynamic<OtherSellersWrapperProps>(
  async () =>
    (
      await import(
        '@/domains/productDiscovery/ProductPage/modules/MainDetails/components/Seller/OtherSellersWrapper'
      )
    ).OtherSellersWrapper,
  { ssr: false },
);

type SellerProps = {
  isRevamp?: boolean;
};

export const Seller: FC<SellerProps> = ({ isRevamp = false }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const { seller, otherSellers, isSellable } = useDetail();
  const brand = useBrand();

  const total = otherSellers?.total || 0;

  const onDisplayOtherSellers = () => {
    Gtm.push({
      event: 'see_alternative_offers',
    });
    setIsModalVisible(true);
  };

  const onHideOtherSellers = () => {
    setIsModalVisible(false);
  };

  const brandLinkUrl = brand.url || '';
  const brandLinkText = discoverTheStore.format({
    brandName: brand?.name || '',
  });

  const sendBrandLinkEvent = () => {
    Gtm.push({
      event: 'interaction_click on a link',
      e_category: 'navigation',
      e_action: 'Click on a link',
      e_label: brandLinkText,
      amp_event_name: 'Click on a link',
      event_trigger: 'product_page',
      redirection_link: brandLinkUrl,
      click_element: brandLinkText,
      interaction_hit: 'False',
    });
  };

  return (
    <>
      {!isRevamp && brand.url && (
        <Link
          href={brandLinkUrl}
          className={classnames(styles.link, styles.brand)}
          onClick={sendBrandLinkEvent}
          onKeyPress={(e) => e.key === 'Enter' && sendBrandLinkEvent()}
          role="link"
          tabIndex={0}
          tag={TypographyLink}
        >
          {brand.imageUrl && (
            <Image
              height={undefined}
              width={undefined}
              src={brand.imageUrl}
              alt="brand"
            />
          )}
          <Text type="small" weight="semiBold">
            {brandLinkText}
          </Text>
        </Link>
      )}

      {isSellable && seller?.name && (
        <Text tag="span" type="small" className={styles.text} weight="semiBold">
          {soldBy}
          <Link href={seller?.url ?? '#'} className={styles.link}>
            {seller?.name}
          </Link>
          {total > 0 &&
            otherVendors.format({
              text: (value) => (
                <span
                  onClick={onDisplayOtherSellers}
                  onKeyPress={onDisplayOtherSellers}
                  className={styles.link}
                  role="link"
                  tabIndex={0}
                  key="key"
                >
                  {value}
                </span>
              ),
              total,
            })}
        </Text>
      )}

      {isModalVisible && isB2C() && (
        <SellersModalB2C onClose={onHideOtherSellers} />
      )}
      {isModalVisible && isB2B() && (
        <SellersModalB2B onHideOtherSellers={onHideOtherSellers} />
      )}
    </>
  );
};

Seller.displayName = 'Seller';
